/**
 
* Port-based utility functions for dashboard routing
 
* Version: 2.1.0 - Dev Domains + Reverse Proxy Support
 
*/
 
// Get port configuration from environment variables with fallbacks

const getPortConfig = () => {

  // Check if we're in development mode by checking the current port

  const currentPort = typeof window !== 'undefined' ? window.location.port : '';

  const isDevelopment = ['3000', '3001', '3002', '3003'].includes(currentPort);



  // Use development ports if in dev mode, otherwise use production ports

  if (isDevelopment) {

    return {

      admin: 3000,

      hospital: 3001,

      fleet: 3002,

      commandCenter: 3003,

    };

  }



  return {

    // Production/External ports from environment variables

    admin: parseInt(process.env.REACT_APP_ADMIN_PORT || '3696', 10),

    hospital: parseInt(process.env.REACT_APP_HOSPITAL_PORT || '3697', 10),

    fleet: parseInt(process.env.REACT_APP_FLEET_PORT || '3698', 10),

    commandCenter: parseInt(process.env.REACT_APP_COMMAND_CENTER_PORT || '3699', 10),

  };

};
 
// Get reverse mapping dynamically
 
const getPortToDashboardMapping = () => {
 
  const ports = getPortConfig();
 
  return {
 
    [ports.admin]: 'admin',
 
    [ports.hospital]: 'hospital',
 
    [ports.fleet]: 'fleet',
 
  };
 
};
 
/**
 
* Get the current port from window.location
 
* Handles both direct port access and reverse proxy/hostname-based routing
 
* @returns {number} - The current port number
 
*/
 
export const getCurrentPort = () => {
 
  if (typeof window === 'undefined') return getPortConfig().admin; // Default for SSR
 
  const hostname = window.location.hostname;
 
  const ports = getPortConfig();
 
  // Map hostnames to their respective ports for reverse proxy scenarios
 
  // ✅ ADDED dev-* domains here
 
  const hostnameToPort = {
 
    // Prod
 
    'metaadmin.jashom.com': ports.admin,

    'metahospital.jashom.com': ports.hospital,

    'metafleet.jashom.com': ports.fleet,

    'metacommandcentre.jashom.com': ports.commandCenter,

    // Dev

    'dev-metaadmin.jashom.com': ports.admin,

    'dev-metahospital.jashom.com': ports.hospital,

    'dev-metafleet.jashom.com': ports.fleet,

    'dev-metacommandcentre.jashom.com': ports.commandCenter,

  };
 
  // Debug logging
 
  console.log('[getCurrentPort] hostname:', hostname);
 
  console.log('[getCurrentPort] hostnameToPort:', hostnameToPort);
 
  console.log('[getCurrentPort] ports config:', ports);
 
  // If hostname matches a known domain, return the corresponding logical port
 
  if (hostnameToPort[hostname]) {
 
    console.log('[getCurrentPort] Matched hostname, returning:', hostnameToPort[hostname]);
 
    return hostnameToPort[hostname];
 
  }
 
  // Otherwise, use the actual port from the URL
 
  const port = window.location.port;
 
  const detectedPort = port
 
    ? parseInt(port, 10)
 
    : (window.location.protocol === 'https:' ? 443 : 80);
 
  console.log('[getCurrentPort] No hostname match, using actual port:', detectedPort);
 
  return detectedPort;
 
};
 
/**

* Get dashboard type based on current hostname/port

* @returns {string} - The dashboard type (admin, hospital, fleet)

*/

export const getDashboardTypeFromPort = () => {

  const hostname = window.location.hostname.toLowerCase();

  // Hostname-based detection (works for dev-* and prod)

  if (hostname.includes('admin')) return 'admin';

  if (hostname.includes('fleet')) return 'fleet';

  if (hostname.includes('command')) return 'commandCenter';

  if (hostname.includes('hospital')) return 'hospital';

  // Fallback for local development (port-based)

  const port = window.location.port;

  // Development ports (from package.json dev scripts)

  if (port === '3000') return 'admin';

  if (port === '3001') return 'hospital';

  if (port === '3002') return 'fleet';

  if (port === '3003') return 'commandCenter';

  // Production/External ports (from .env configuration)

  if (port === '3696') return 'admin';

  if (port === '3697') return 'hospital';

  if (port === '3698') return 'fleet';

  if (port === '3699') return 'commandCenter';

  return 'admin'; // default

};
 
/**
 
* Check if user should be redirected based on port and role
 
* @param {string} userRole - The user's role
 
* @param {string} dashboardType - The current dashboard type
 
* @returns {boolean} - Whether user should be redirected
 
*/
 
export const shouldRedirectUser = (userRole, dashboardType) => {

  // Single-domain migration: this used to force a logout + "wrong
  // subdomain, go to metahospital.jashom.com instead" screen whenever a
  // user's role didn't match whichever of the 4 subdomains/ports they
  // happened to be on. There's only one domain now, so that check no
  // longer makes sense — role-based access is enforced by ProtectedRoute's
  // requiredRoles instead. Every caller of this function (AuthContext.js,
  // RoleBasedRedirect.jsx) falls through to its normal "let the user
  // through" path once this always returns false, so neither of those
  // files needed to change. Kept as a function (not deleted/inlined) so
  // this stays a single, obvious place to look if subdomain-splitting is
  // ever reintroduced.

  return false;

};
 
/**
 
* Get expected dashboard type for a user role
 
* @param {string} userRole - The user's role
 
* @returns {string} - The expected dashboard type
 
*/
 
export const getExpectedDashboardForRole = (userRole) => {
 
  switch (userRole) {
 
    case 'SUPER_ADMIN':
 
    case 'SUPER_ADMIN_USER':
 
      return 'admin';
 
    case 'HOSPITAL_USER':
 
      return 'hospital';
 
    case 'TENANT_ADMIN':

    case 'TENANT_USER':

      return 'fleet';

    case 'COMMAND_CENTRE_USER':

      return 'commandCenter';

    default:

      return 'admin';

  }

};
 
/**
 
* Get the port number for a dashboard type
 
* @param {string} dashboardType - The dashboard type
 
* @returns {number} - The port number
 
*/
 
export const getPortForDashboard = (dashboardType) => {
 
  const ports = getPortConfig();
 
  return ports[dashboardType] || ports.admin;
 
};
 
/**
 
* Get the full URL for a dashboard type
 
* Handles both direct port access and reverse proxy/hostname-based routing
 
* @param {string} dashboardType - The dashboard type
 
* @param {string} path - Optional path to append
 
* @returns {string} - The full URL
 
*/
 
export const getUrlForDashboard = (dashboardType, path = '/dashboard') => {
 
  const protocol = window.location.protocol;
 
  const currentHostname = window.location.hostname;
 
  const isDevDomain = currentHostname.startsWith('dev-');
 
  // ✅ UPDATED: dev domains stay in dev-*, prod uses non-dev
 
  const dashboardToHostname = isDevDomain

    ? {

        admin: 'dev-metaadmin.jashom.com',

        hospital: 'dev-metahospital.jashom.com',

        fleet: 'dev-metafleet.jashom.com',

        commandCenter: 'dev-metacommandcentre.jashom.com',

      }

    : {

        admin: 'metaadmin.jashom.com',

        hospital: 'metahospital.jashom.com',

        fleet: 'metafleet.jashom.com',

        commandCenter: 'metacommandcentre.jashom.com',

      };
 
  // Check if we're on a jashom.com domain
 
  if (currentHostname.includes('jashom.com')) {
 
    const targetHostname = dashboardToHostname[dashboardType];
 
    return `${protocol}//${targetHostname}${path}`;
 
  }
 
  // For local development / IP, use port-based routing
 
  const port = getPortForDashboard(dashboardType);
 
  return `${protocol}//${currentHostname}:${port}${path}`;
 
};
 
/**
 
* Get all configured ports
 
* @returns {Object} - Object with port configuration
 
*/
 
export const getAllPorts = () => {
 
  return getPortConfig();
 
};
 
/**
 
* Get port names mapping for display purposes
 
* @returns {Object} - Object with port numbers as keys and dashboard names as values
 
*/
 
export const getPortNames = () => {
 
  const ports = getPortConfig();
 
  return {
 
    [ports.admin]: 'Admin Dashboard',

    [ports.hospital]: 'Hospital Dashboard',

    [ports.fleet]: 'Fleet Dashboard',

    [ports.commandCenter]: 'Command Center Dashboard',

  };

};